import { Fragment, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner";
import axiosInstance from "../../api/axiosInstance";
import { Download } from "lucide-react";
import { propertyTypeLabels } from "../../utils/common";

const scopeLabels = {
  city: "City",
  zip: "ZIP Code",
  county: "County",
};

const limitOptions = [
  // { label: "1", value: 1 },
  // { label: "2", value: 2 },
  // { label: "3", value: 3 },
  { label: "10", value: 10 },
  { label: "20", value: 20 },
  { label: "30", value: 30 },
  { label: "50", value: 50 },
  { label: "100", value: 100 },
  { label: "200", value: 200 },
  { label: "300", value: 300 },
];

const AddressOwnerGroups = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [openRows, setOpenRows] = useState({});
  const [exporting, setExporting] = useState(false);

  // Form state – only address and limit values
  const [formData, setFormData] = useState({
    address: searchParams.get("address") || "",
    flippersLimit: Number(searchParams.get("flippersLimit")) || 10,
    landlordsLimit: Number(searchParams.get("landlordsLimit")) || 10,
    property_type: searchParams.get("property_type") || "",
    // addressLimit: Number(searchParams.get("addressLimit")) || 5,
  });

  // Sync form state when URL changes (browser back/forward)
  useEffect(() => {
    setFormData({
      address: searchParams.get("address") || "",
      flippersLimit: Number(searchParams.get("flippersLimit")) || 10,
      landlordsLimit: Number(searchParams.get("landlordsLimit")) || 10,
      property_type: searchParams.get("property_type") || "",
      // addressLimit: Number(searchParams.get("addressLimit")) || 5,
    });
  }, [searchParams]);

  // Fetch data whenever relevant URL params change
  useEffect(() => {
    const fetchData = async () => {
      const addressParam = searchParams.get("address");
      if (!addressParam?.trim()) {
        setError("Address is required in URL query parameter.");
        setData(null);
        return;
      }

      try {
        setLoading(true);
        setData(null);
        setError("");
        const params = {
          address: addressParam,
          flippers_limit: searchParams.get("flippersLimit") || undefined,
          landlords_limit: searchParams.get("landlordsLimit") || undefined,
          property_type: searchParams.get("property_type") || undefined,
          // address_limit: searchParams.get("addressLimit") || undefined,
        };
        const resp = await axiosInstance.get(`/api/dealmachine_properties/public/address-groups`, { params });
        setData(resp.data || null);
      } catch (err) {
        setData(null);
        setError(err.response?.data?.message || "Failed to load address data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [searchParams]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const newParams = new URLSearchParams();

    if (formData.address.trim()) newParams.set("address", formData.address.trim());
    if (formData.flippersLimit) newParams.set("flippersLimit", formData.flippersLimit);
    if (formData.landlordsLimit) newParams.set("landlordsLimit", formData.landlordsLimit);
    if (formData.property_type) newParams.set("property_type", formData.property_type);
    // if (formData.addressLimit) newParams.set("addressLimit", formData.addressLimit);

    setSearchParams(newParams);
  };

  const toggleRow = (key) => {
    setOpenRows((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const downloadCSV = () => {
    try {
      if (!data) return;
      setExporting(true);

      const csvRows = [];
      const headers = ["Type", "Name", "Location", "Mailing Address", "Count"];

      ["flippers", "landlords"].forEach((typeKey) => {
        const typeLabel = typeKey === "flippers" ? "Flipper" : "Landlord";
        const typeData = data[typeKey] || {};

        // Iterate over city, zip, county scopes
        Object.entries(scopeLabels).forEach(([scopeKey, scopeLabel]) => {
          const scopeData = typeData[scopeKey] || {};
          const rows = Array.isArray(scopeData) ? scopeData : scopeData.rows || [];
          rows.forEach((row) => {
            csvRows.push({
              Type: typeLabel,
              Name: row.name || "Unknown",
              Location: scopeLabel,
              "Mailing Address": row.mailing_address || "Not Found",
              Count: row.count || 0,
            });
          });
        });
      });

      if (csvRows.length === 0) return;

      const escapeCSV = (value) => {
        if (value === null || value === undefined) return "";
        const stringValue = String(value).replace(/"/g, '""');
        return `"${stringValue}"`;
      };

      const csvContent = [headers.map(escapeCSV).join(","), ...csvRows.map((row) => headers.map((h) => escapeCSV(row[h])).join(","))].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `address-owner-data-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export error", err);
    } finally {
      setExporting(false);
    }
  };

  const renderTable = (title, tableData = {}, sectionKey, scopeKey) => {
    const rows = Array.isArray(tableData) ? tableData : tableData?.rows || [];
    const total = Array.isArray(tableData) ? rows.length : tableData?.total || 0;

    return (
      <div key={`${sectionKey}-${scopeKey}`} className="!border !border-gray-300 rounded bg-white overflow-hidden">
        <div className="!px-4 !py-3 bg-gray-100 font-semibold text-left">
          {title} ({total} total)
        </div>
        <div className="overflow-x-auto">
          <table className="!w-full !border-collapse">
            <thead>
              <tr className="!bg-gray-200">
                <th className="!border !border-gray-300 !px-3 !py-2 text-left">{sectionKey === "flippers" ? "Flip Owner Name" : "Landlord Name"}</th>
                <th className="!border !border-gray-300 !px-3 !py-2 text-center !w-24">Count</th>
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows.map((row, index) => {
                  const rowKey = `${sectionKey}-${scopeKey}-${index}`;
                  const isOpen = !!openRows[rowKey];
                  return (
                    <Fragment key={rowKey}>
                      <tr>
                        <td className="!border !border-gray-300 !px-3 !py-2 text-left align-top">
                          <div className="font-medium text-gray-900">{row.name || "Unknown"}</div>
                          <div className="text-xs text-gray-500 !mt-1">Mailing: {row.mailing_address || "Not Found"}</div>
                        </td>
                        <td className="!border !border-gray-300 !px-3 !py-2 text-center align-top">
                          <button type="button" className="text-blue-700 hover:text-blue-900 underline font-medium" onClick={() => toggleRow(rowKey)}>
                            {row.count || 0}
                          </button>
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={2} className="!border !border-gray-300 !px-3 !py-2 text-left bg-gray-50">
                            <div className="text-sm font-medium !mb-2">Counted Addresses</div>
                            {row.addresses?.length > 0 ? (
                              <>
                                {row.addresses_total > row.addresses.length && (
                                  <div className="text-xs text-gray-500 !mb-2">
                                    Showing first {row.addresses.length} of {row.addresses_total} addresses.
                                  </div>
                                )}
                                <ul className="list-disc !pl-5 text-sm text-gray-700 !space-y-1">
                                  {row.addresses.map((property, propertyIndex) => (
                                    <li key={`${rowKey}-${property.property_id || propertyIndex}`}>{property.label || property.address || "Unknown Address"}</li>
                                  ))}
                                </ul>
                              </>
                            ) : (
                              <div className="text-sm text-gray-500">No addresses found.</div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={2} className="!border !border-gray-300 !px-3 !py-4 text-center text-gray-500">
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderSection = (title, sectionKey) => (
    <section className="!mb-10">
      <h2 className="text-2xl font-semibold text-left !mb-4">{title}</h2>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">{Object.keys(scopeLabels).map((scopeKey) => renderTable(scopeLabels[scopeKey], data?.[sectionKey]?.[scopeKey] || {}, sectionKey, scopeKey))}</div>
    </section>
  );

  return (
    <>
      <div className="input_fields_container">
        <form className="input_fields_container SP_search_container !grid sm:!grid-cols-2 md:!grid-cols-3 lg:!grid-cols-4 gap-2 sm:gap-4 md:gap-6" onSubmit={handleSubmit}>
          <input type="text" name="address" className="input_field" value={formData.address} onChange={(e) => handleInputChange("address", e.target.value)} placeholder="Enter address" required />

          {/* Flippers Limit */}
          <div className="input_field flex flex-row justify-between items-center gap-1">
            <label className="text-sm font-medium text-gray-700">Flippers</label>
            <select className="outline-none cursor-pointer" value={formData.flippersLimit} onChange={(e) => handleInputChange("flippersLimit", Number(e.target.value))}>
              {limitOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          {/* Landlords Limit */}
          <div className="input_field flex flex-row justify-between items-center gap-1">
            <label className="text-sm font-medium text-gray-700">Landlords</label>
            <select className="outline-none cursor-pointer" value={formData.landlordsLimit} onChange={(e) => handleInputChange("landlordsLimit", Number(e.target.value))}>
              {limitOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="input_field">
            <select name="property_type" className={`cursor-pointer w-full focus:!outline-none ${formData.property_type ? "text-gray-700" : "text-gray-500"}`} value={formData.property_type || ""} onChange={(e) => handleInputChange("property_type", e.target.value)}>
              <option value="">-- Select Property Type --</option>
              {Object.entries(propertyTypeLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2 md:col-span-3 lg:col-span-4 flex justify-center">
            <button className="input_button" type="submit" disabled={loading}>
              {loading ? "Loading..." : "Search"}
            </button>
          </div>
        </form>
      </div>

      <div className="min-h-screen bg-gray-50 !px-4 !py-6 md:!px-8">
        <div className="max-w-7xl !mx-auto">
          <div className="!mb-6 text-left flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Address Owner Data</h1>
              <div className="text-gray-600 !mt-2">{searchParams.get("address") || "No address provided"}</div>
              {data?.subjectProperty && <div className="text-sm text-gray-500 !mt-1">Matched: {data.subjectProperty.label || `${data.subjectProperty.address}, ${data.subjectProperty.city}, ${data.subjectProperty.state} ${data.subjectProperty.zip}`}</div>}
            </div>
            <div>
              {data && (
                <button onClick={downloadCSV} disabled={exporting} className="flex items-center gap-1 px-2 py-1 rounded-sm text-xs border border-[#008080] bg-[#008080] text-white hover:bg-[#005252] hover:border-[#005252] disabled:bg-sky-200 disabled:text-black disabled:cursor-wait ease-in-out duration-200 transition-all">
                  <span>
                    <Download size={15} />
                  </span>
                  <span>{exporting ? "Exporting..." : "Export Data"}</span>
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <div className="w-full text-center !py-12">
              <LoadingSpinner size={30} textSize={20} />
            </div>
          ) : error ? (
            <div className="!border !border-red-300 bg-red-50 text-red-700 !px-4 !py-3 rounded text-left">{error}</div>
          ) : data ? (
            <>
              {renderSection("Flippers", "flippers")}
              {renderSection("Landlords", "landlords")}
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

export default AddressOwnerGroups;
